import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import artieLogo from "@/assets/images/artie.png";

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(data: ForgotPasswordFormValues) {
    setIsLoading(true);
    try {
      await apiClient.requestPasswordReset(data.email);
      setSubmittedEmail(data.email);
      setIsSubmitted(true);
      toast.success("Reset link sent to your email");
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to send reset link");
    } finally {
      setIsLoading(false);
    }
  }

  const handleRetry = () => {
    setIsSubmitted(false);
    setSubmittedEmail("");
    form.reset();
  };

  return (
    <div className="min-h-screen antialiased bg-white text-neutral-900 flex flex-col items-center justify-center relative overflow-hidden font-sans">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
      `}</style>


      {/* Main Content Card */}
      <main className="w-full max-w-[400px] px-4 relative z-10">
        
        {/* Header Logo */}
        <div className="flex justify-center mb-8 animate-fade-in">
          <Link to="/" className="flex items-center gap-2 group transition-opacity hover:opacity-80">
            <img src={artieLogo} alt="Artie" className="w-10 h-10 rounded-xl shadow-lg shadow-neutral-900/10" />
          </Link>
        </div>

        {!isSubmitted ? (
          /* State: Request Form */
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-neutral-100 p-8 sm:p-10 animate-scale-in">
            <div className="text-center mb-8">
              <h1 className="text-xl font-semibold tracking-tight text-neutral-900 mb-2">Forgot your password?</h1>
              <p className="text-neutral-500 text-sm leading-relaxed">
                No worries. Enter your email and we'll send you reset instructions.
              </p>
            </div>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-1.5 text-left">
                <label htmlFor="email" className="block text-sm font-medium text-neutral-700">Email address</label>
                <div className="relative">
                  <input 
                    {...form.register("email")}
                    type="email" 
                    id="email" 
                    placeholder="you@example.com" 
                    className="block w-full h-11 px-3.5 rounded-lg border border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900 focus:outline-none transition-all duration-200 shadow-sm text-sm"
                  />
                  {form.formState.errors.email && (
                    <p className="text-red-500 text-xs mt-1">{form.formState.errors.email.message}</p>
                  )}
                </div>
              </div>

              <button 
                type="submit" 
                disabled={isLoading}
                className="w-full flex items-center justify-center h-11 px-4 rounded-lg text-white font-medium bg-neutral-900 hover:bg-neutral-800 active:scale-[0.98] transition-all duration-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-900 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                ) : (
                  <span>Send reset link</span>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="inline-flex items-center text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors group">
                <ArrowLeft className="w-4 h-4 mr-1.5 transition-transform group-hover:-translate-x-1" />
                Back to login
              </Link>
            </div>
          </div>
        ) : (
          /* State: Success Message */
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-neutral-100 p-8 sm:p-10 animate-scale-in">
            <div className="flex flex-col items-center text-center">
              {/* Success Icon */}
              <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-6 ring-4 ring-emerald-50/50">
                <Check className="w-6 h-6 text-emerald-500" />
              </div>
              
              <h2 className="text-xl font-semibold tracking-tight text-neutral-900 mb-3">Check your email</h2>
              
              <p className="text-neutral-500 text-sm leading-relaxed mb-8">
                If an account exists for <span className="font-medium text-neutral-900">{submittedEmail}</span>, you'll receive password reset instructions shortly.
              </p>

              <Link to="/login" className="w-full flex items-center justify-center h-11 px-4 rounded-lg bg-white border border-neutral-200 text-neutral-700 font-medium hover:bg-neutral-50 hover:text-neutral-900 active:bg-neutral-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-200 text-sm">
                Return to login
              </Link>
              
              <div className="mt-6 text-center">
                <p className="text-xs text-neutral-400">
                  Didn't receive an email? <button type="button" onClick={handleRetry} className="text-neutral-600 hover:text-neutral-900 font-medium hover:underline transition-all">Click to try again</button>
                </p>
              </div>
            </div>
          </div>
        )}

      </main>

      {/* Footer Info */}
      <footer className="absolute bottom-6 text-center w-full p-4">
        <p className="text-xs text-neutral-400">
          © Artie Inc. Secure access.
        </p>
      </footer>
    </div>
  );
}